import EmailOutlined from '@mui/icons-material/EmailOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import { paperClasses, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import { LoadDigestLoadFilter } from '~hooks/useLoadDigests/models';

import DispatchTriggerButton from '../DispatchTriggerButton';
import LoadDigestViewDialog from './LoadDigestViewDialog';
import useLoadDigestAccess from './useLoadDigestAccess';

const menuIconSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadBlack,
  fontSize: '16px',
});

interface LoadDigestButtonProps {
  disabledTooltip?: string;
  isDisabled?: boolean;
  onEmail: () => void;
  onSelect?: () => void;
  onView: (loadFilter: LoadDigestLoadFilter) => Promise<void>;
  placement: 'bulkActionsBar' | 'exportMenu';
}

const LoadDigestButton = observer(
  ({
    disabledTooltip,
    isDisabled,
    onEmail,
    onSelect,
    onView,
    placement,
  }: LoadDigestButtonProps) => {
    const { canEmail, canView } = useLoadDigestAccess();
    const menuHandler = useRef<MenuHandler>(null);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [isRequestingRender, setIsRequestingRender] = useState(false);

    if (!(canView || canEmail)) {
      return null;
    }

    const handleView = async (loadFilter: LoadDigestLoadFilter) => {
      setIsViewDialogOpen(false);
      setIsRequestingRender(true);

      try {
        await onView(loadFilter);
      } finally {
        setIsRequestingRender(false);
      }
    };

    const menuItems = [
      {
        icon: <PictureAsPdfOutlined sx={menuIconSx} />,
        isAllowed: canView,
        key: 'view',
        label: t('dispatch.load_digest.view_title'),
        onClick: () => setIsViewDialogOpen(true),
      },
      {
        icon: <EmailOutlined sx={menuIconSx} />,
        isAllowed: canEmail,
        key: 'email',
        label: t('dispatch.load_digest.email_title'),
        onClick: onEmail,
      },
    ].filter(({ isAllowed }) => isAllowed);

    const isTooltipVisible = Boolean(isDisabled && disabledTooltip);

    return (
      <>
        <Tooltip
          disableFocusListener={!isTooltipVisible}
          disableHoverListener={!isTooltipVisible}
          disableTouchListener={!isTooltipVisible}
          title={disabledTooltip ?? ''}
        >
          <Box
            component="span"
            data-test-id="load-digest-button-tooltip-anchor"
            sx={{ display: 'flex' }}
          >
            <Menu
              ref={menuHandler}
              menuTrigger={
                <DispatchTriggerButton
                  data-test-id="load-digest-button"
                  disabled={isDisabled}
                  loading={isRequestingRender}
                  placement={placement}
                  startIcon={<PictureAsPdfOutlined />}
                >
                  {t('dispatch.load_digest.button')}
                </DispatchTriggerButton>
              }
              sx={{ [`& .${paperClasses.root}`]: { width: '175px' } }}
            >
              {menuItems.map((item) => (
                <MenuItem
                  key={item.key}
                  onClick={() => {
                    item.onClick();
                    menuHandler.current?.onClose?.();
                    onSelect?.();
                  }}
                >
                  <Box
                    sx={{ alignItems: 'center', display: 'flex', gap: 1, width: '100%' }}
                  >
                    {item.icon}
                    <Typography
                      variant="subtitle2"
                      sx={(theme) => ({ color: theme.brandV2.colors.treadBlack })}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Tooltip>

        <LoadDigestViewDialog
          isOpen={isViewDialogOpen}
          onClose={() => setIsViewDialogOpen(false)}
          onView={handleView}
        />
      </>
    );
  },
);

export default LoadDigestButton;
